import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, ChevronDown, Sliders, BookOpen } from 'lucide-react';

import { getCurrentTenant, getCurrentUser, getJwtFromCookie } from '../utils/jwt-parser';
import { useAuth } from '@/features/platform/contexts/auth-context';
// import { useAuth } from '@/features/platform/contexts/user-auth-context';
// import { getJwtFromCookie, getCurrentUser, getCurrentTenant } from '@/features/auth/utils/jwt-parser';

/**
 * UserMenu - Displays current user info and provides logout option
 * Shows admin badge if user is an admin
 */
export const UserMenu: React.FC = () => {
    const { user, logout } = useAuth();
    const isAdmin = true;
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Get user info from JWT if available
    const token = getJwtFromCookie('argus_token');
    const jwtUser = token ? getCurrentUser(token) : null;
    const tenant = token ? getCurrentTenant(token) : null;

    const displayName = user?.name || jwtUser?.name || 'User';
    const displayEmail = user?.email || jwtUser?.email || '';

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (!user && !jwtUser) {
        return null;
    }

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const initials = displayName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2) || displayEmail[0]?.toUpperCase() || '?';

    return (
        <div className="relative inline-block" ref={menuRef}>
            {/* User Avatar Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={displayEmail}
            >
                {user?.image_data_url ? (
                    <img
                        src={user.image_data_url}
                        alt={displayName}
                        className="w-9 h-9 rounded-full ring-2 ring-white dark:ring-slate-950 shadow-sm"
                    />
                ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-orange-400 to-orange-600 text-white flex items-center justify-center text-sm font-bold shadow-sm ring-2 ring-white dark:ring-slate-950">
                        {initials}
                    </div>
                )}
                <div className="hidden lg:flex flex-col items-start">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
                        {displayName}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 leading-tight">
                        {tenant?.slug || displayEmail}
                    </span>
                </div>
                {isAdmin && (
                    <span className="px-2 py-1 text-xs font-semibold text-white bg-red-600 rounded-full hidden lg:inline">
                        Admin
                    </span>
                )}
                <ChevronDown
                    className={`w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Menu */}
                    <div className="absolute top-full right-0 mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 w-64 overflow-hidden">
                        {/* User Info Header */}
                        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                                    {initials}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                        {displayName}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {displayEmail}
                                    </p>
                                    {tenant && (
                                        <p className="text-xs text-orange-600 dark:text-orange-400 truncate mt-0.5">
                                            {tenant.slug}
                                        </p>
                                    )}
                                    {isAdmin && (
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                                            Administrator
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Menu Items */}
                        {/* <div className="py-2">
                            <Link
                                to="/profile"
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                <User className="w-4 h-4" />
                                <span>Profile</span>
                            </Link>
                            <Link
                                to="/settings"
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                <Settings className="w-4 h-4" />
                                <span>Settings</span>
                            </Link> */}

                        {/* Admin Section */}
                        {/* {isAdmin && (
                                <>
                                    <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
                                    <Link
                                        to="/admin"
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors font-semibold"
                                    >
                                        Admin Panel
                                    </Link>
                                </>
                            )}
                        </div> */}

                        {/* Menu Items */}
                        <div className="py-1">
                            <Link
                                to="/m/preferences"
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
                            >
                                <Sliders className="w-4 h-4 text-slate-400" />
                                <span>Preferences</span>
                            </Link>
                            <Link
                                to="/dev/docs/platform"
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
                            >
                                <BookOpen className="w-4 h-4 text-slate-400" />
                                <span>Developer docs</span>
                            </Link>
                        </div>

                        {/* Logout */}
                        <div className="border-t border-slate-200 dark:border-slate-700 py-2">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default UserMenu;
